import React from 'react';
import type { EllmoApiType } from './shared';

export type { EllmoApiType };

export interface EllmoApiProps {
  /**
   * The URL path to fetch data for (required).
   * 
   * With middleware (recommended):
   * ```tsx
   * import { headers } from 'next/headers';
   * import { getPathnameFromHeaders } from 'ellmo-ai-react/middleware';
   * 
   * const pathname = getPathnameFromHeaders(headers());
   * <EllmoApi clientId="..." url={pathname || '/'} />
   * ```
   * 
   * Without middleware:
   * ```tsx
   * <EllmoApi clientId="..." url="/about" />
   * ```
   */
  url: string;
  /**
   * The API key/client identifier (required)
   */
  clientId: string;
  /**
   * Type of data to fetch
   */
  type?: EllmoApiType;
  /**
   * Custom className for styling
   */
  className?: string;
  /**
   * Whether to show errors
   */
  showErrors?: boolean;
  /**
   * Children to render when data is available
   */
  children?: (data: any, error: Error | null) => React.ReactNode;
}

/**
 * Server-only Ellmo API component (React Server Component)
 * This component fetches data during server rendering.
 * It requires Next.js 13+ App Router or other RSC-compatible frameworks.
 * 
 * @example
 * ```tsx
 * // In a server component (app/page.tsx)
 * export default async function Page() {
 *   return <EllmoApi clientId="your-id" url="/about" />;
 * }
 * ```
 */
const EllmoApi = async ({
  url,
  clientId,
  type,
  className = '',
  showErrors = false,
  children
}: EllmoApiProps): Promise<React.ReactElement | null> => {
  let data: any = null;
  let error: Error | null = null;

  let translatedType = type === 'structured' ? 'structured' : 'semantic';

  try {
    // Validate required props
    if (!clientId) {
      throw new Error('clientId is required. Please provide your Ellmo API client identifier.');
    }

    if (!url) {
      throw new Error('url prop is required. Please provide the page pathname.');
    }

    // Encode the URL path for the API call
    const encodedUrl = encodeURIComponent(url);

    // Build the API URL
    const apiUrl = `https://www.tryellmo.ai/public-api/wordpress?c=${clientId}&t=${translatedType}&u=${encodedUrl}`;

    // Fetch data during server render
    const response = await fetch(apiUrl, {
      cache: 'no-store', // Always fetch fresh data
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    if (type === 'structured') {
      data = await response.text();
    } else if (type === 'semantic') {
      data = await response.text();
    } else {
      throw new Error(`Invalid type: ${type}`);
    }
  } catch (err) {
    error = err instanceof Error ? err : new Error('Unknown error occurred');
  }

  // If children prop is provided, use render prop pattern
  if (children) {
    return <>{children(data, error)}</>;
  }

  // Show errors if enabled
  if (error && showErrors) {
    return (
      <div className={`ellmo-api-error ${className}`}>
        <div>Error: {error.message}</div>
      </div>
    );
  }

  // Show data if showErrors is enabled (for debugging)
  if (data && showErrors) {
    return (
      <div className={`ellmo-api-data ${className}`}>
        <pre>{JSON.stringify(data, null, 2)}</pre>
      </div>
    );
  }

  // Invisible component by default - just fetches data during server render
  if (type === 'structured' && data) {
    return <div dangerouslySetInnerHTML={{ __html: data }} />;
  } else if (type === 'semantic' && data) {
    return <div dangerouslySetInnerHTML={{ __html: data }} />;
  }
  return null;
};

export default EllmoApi;
